import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import {
  BrowserCacheInvalidationEvent,
  BrowserCacheLeaseClaimResult,
  BrowserCacheLeaseCoordinator,
  BrowserCacheLeaseState,
} from '../../coordination/broadcast-lease';
import { BrowserDataCache, BrowserDataCacheOptions, createBrowserDataCache } from '../../core/browser-data-cache';
import { BrowserDataCacheSyncJob, BrowserDataCacheSyncOptions } from '../../core/types';

export interface BrowserDataCacheAngularAdapterOptions {
  channelName: string;
  resourceId: string;
  cache?: BrowserDataCache;
  cacheOptions?: BrowserDataCacheOptions;
}

@Injectable()
export class BrowserDataCacheAngularService implements OnDestroy {
  readonly leaseState$: BehaviorSubject<BrowserCacheLeaseState>;
  readonly cacheInvalidated$ = new Subject<BrowserCacheInvalidationEvent>();

  protected coordinator: BrowserCacheLeaseCoordinator | null = null;
  protected cache: BrowserDataCache | null = null;
  private unsubscribeState: (() => void) | null = null;
  private unsubscribeInvalidation: (() => void) | null = null;

  configure(options: BrowserDataCacheAngularAdapterOptions): void {
    this.disposeCoordinator();
    this.cache = options.cache || (options.cacheOptions ? createBrowserDataCache(options.cacheOptions) : null);
    this.coordinator = new BrowserCacheLeaseCoordinator({
      channelName: options.channelName,
      resourceId: options.resourceId,
    });
    this.leaseState$.next(this.coordinator.getState());
    this.unsubscribeState = this.coordinator.onState((state) => this.leaseState$.next(state));
    this.unsubscribeInvalidation = this.coordinator.onInvalidation((event) => this.cacheInvalidated$.next(event));
  }

  constructor() {
    this.leaseState$ = new BehaviorSubject<BrowserCacheLeaseState>({
      status: 'idle',
      resourceId: '',
      ownerId: null,
      leaseUntil: null,
      channelAvailable: false,
    });
  }

  get ownerId(): string {
    return this.requireCoordinator().ownerId;
  }

  claimWriteLease(signal?: AbortSignal): Promise<BrowserCacheLeaseClaimResult> {
    return this.requireCoordinator().claim(signal);
  }

  releaseWriteLease(): void {
    this.coordinator?.release();
  }

  broadcastCacheInvalidated(tables: string[], reason?: string): void {
    this.requireCoordinator().broadcastInvalidation(tables, reason);
  }

  startSync(sourceId: string, options?: BrowserDataCacheSyncOptions): BrowserDataCacheSyncJob {
    if (!this.cache) {
      throw new Error('BrowserDataCacheAngularService is not configured with a cache.');
    }
    return this.cache.startSync(sourceId, options);
  }

  ngOnDestroy(): void {
    this.disposeCoordinator();
    this.leaseState$.complete();
    this.cacheInvalidated$.complete();
  }

  private requireCoordinator(): BrowserCacheLeaseCoordinator {
    if (!this.coordinator) {
      throw new Error('BrowserDataCacheAngularService is not configured.');
    }
    return this.coordinator;
  }

  private disposeCoordinator(): void {
    this.unsubscribeState?.();
    this.unsubscribeInvalidation?.();
    this.unsubscribeState = null;
    this.unsubscribeInvalidation = null;
    this.coordinator?.dispose();
    this.coordinator = null;
  }
}
